"use client";

import { type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

export type BadgeTone = "neutral" | "teal" | "amber" | "rose" | "ink" | "emerald";

export function Badge({
  children,
  tone = "neutral",
  className,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
}) {
  const tones: Record<BadgeTone, string> = {
    neutral: "bg-ink-100 text-ink-700 ring-1 ring-inset ring-ink-200",
    teal: "bg-teal-100 text-teal-800 ring-1 ring-inset ring-teal-200",
    amber: "bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-200",
    rose: "bg-rose-100 text-rose-800 ring-1 ring-inset ring-rose-200",
    ink: "bg-ink-800 text-white",
    emerald: "bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-200",
  };
  return <span className={cn("inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold", tones[tone], className)}>{children}</span>;
}

export function Button({
  children,
  variant = "primary",
  className,
  type,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
}) {
  const variants: Record<"primary" | "secondary" | "ghost" | "danger", string> = {
    primary: "bg-ink-900 text-white shadow-soft hover:bg-ink-800",
    secondary: "bg-white text-ink-800 ring-1 ring-inset ring-ink-200 hover:bg-ink-50",
    ghost: "bg-transparent text-ink-700 hover:bg-ink-100",
    danger: "bg-rose-600 text-white hover:bg-rose-500",
  };
  return (
    <button
      type={type ?? "button"}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        variants[variant],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function Card({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
}) {
  return <div {...props} className={cn("rounded-3xl border border-white/60 bg-white/85 p-5 shadow-soft backdrop-blur", className)}>{children}</div>;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="max-w-3xl">
        {eyebrow ? <p className="text-xs font-semibold uppercase tracking-[0.3em] text-teal-700">{eyebrow}</p> : null}
        <h1 className="mt-2 text-3xl font-black tracking-tight text-ink-900 sm:text-4xl">{title}</h1>
        {description ? <p className="mt-3 text-sm leading-7 text-ink-600 sm:text-base">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function SectionHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h2 className="text-lg font-bold text-ink-900">{title}</h2>
        {description ? <p className="mt-1 text-sm text-ink-600">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function MetricCard({
  label,
  value,
  sublabel,
  tone = "neutral",
}: {
  label: string;
  value: string | number;
  sublabel?: string;
  tone?: BadgeTone;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-ink-500">{label}</p>
          <p className="mt-2 text-2xl font-black text-ink-900">{value}</p>
          {sublabel ? <p className="mt-1 text-sm text-ink-500">{sublabel}</p> : null}
        </div>
        <Badge tone={tone}>{label}</Badge>
      </div>
    </Card>
  );
}

export function Tabs({
  value,
  onValueChange,
  items,
}: {
  value: string;
  onValueChange: (value: string) => void;
  items: { value: string; label: string; count?: number }[];
}) {
  return (
    <div className="flex gap-2 overflow-x-auto rounded-2xl bg-white/70 p-1 ring-1 ring-inset ring-ink-200">
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            onClick={() => onValueChange(item.value)}
            className={cn(
              "inline-flex min-w-0 shrink-0 items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition",
              active ? "bg-ink-900 text-white shadow-soft" : "text-ink-600 hover:bg-ink-100",
            )}
          >
            <span>{item.label}</span>
            {typeof item.count === "number" ? <span className={cn("rounded-full px-2 py-0.5 text-xs", active ? "bg-white/20" : "bg-ink-200 text-ink-700")}>{item.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function FieldLabel({ children }: { children: ReactNode }) {
  return <label className="mb-1.5 block text-sm font-semibold text-ink-800">{children}</label>;
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cn("w-full rounded-2xl border border-ink-200 bg-white px-4 py-3 text-sm text-ink-900 outline-none transition placeholder:text-ink-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20", props.className)} />;
}

export function SelectInput(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cn("w-full rounded-2xl border border-ink-200 bg-white px-4 py-3 text-sm text-ink-900 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20", props.className)} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cn("min-h-[120px] w-full rounded-2xl border border-ink-200 bg-white px-4 py-3 text-sm text-ink-900 outline-none transition placeholder:text-ink-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20", props.className)} />;
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-3xl border border-dashed border-ink-200 bg-ink-50/60 p-8 text-center">
      <p className="text-base font-bold text-ink-900">{title}</p>
      <p className="mx-auto mt-2 max-w-xl text-sm leading-7 text-ink-600">{description}</p>
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

export function DataTable({
  columns,
  rows,
}: {
  columns: string[];
  rows: ReactNode[][];
}) {
  return (
    <div className="overflow-x-auto rounded-3xl border border-ink-200 bg-white shadow-soft">
      <table className="min-w-full divide-y divide-ink-200 text-left text-sm">
        <thead className="bg-ink-50">
          <tr>
            {columns.map((column) => (
              <th key={column} className="px-4 py-3 font-semibold text-ink-700">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="align-top">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-4 py-3 text-ink-700">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!mounted || !open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mounted, open]);

  if (!open || !mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 p-4">
      <button
        type="button"
        aria-label="모달 배경 닫기"
        className="absolute inset-0 bg-ink-950/55"
        onClick={onClose}
      />
      <div className="relative z-10 flex h-full items-center justify-center">
        <div
          className="max-h-[90vh] w-full max-w-5xl overflow-auto rounded-[28px] bg-white shadow-2xl shadow-ink-950/20"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-ink-100 bg-white/95 px-5 py-4 backdrop-blur">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-700">상세 보기</p>
              <h3 className="mt-1 text-lg font-bold text-ink-900">{title}</h3>
            </div>
            <Button variant="ghost" onClick={onClose}>
              닫기
            </Button>
          </div>
          <div className="p-5">{children}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
